// Everything the page calls on the client core. Actions are fire-and-forget: the server answers through the session,
// which the next frame shows.
import { ChatChannel, NoticeKind } from '../../../shared/src/constants';
import { contentVersion } from '../core/content';
import { validateCharacterName } from '../../../shared/src/rules/names';
import { contentForPage } from './contentExport';
import { game } from './game';
import type { SpacetimeOptions } from './stdbLogin';
import { chat, social, useProfanityList } from './ui';
import * as W from './windows';

const S = () => game.session;

export const webApi = {
  // ---- start-up ------------------------------------------------------------------------------------------------
  async init(names: string[], texts: string[]) {
    const i = names.indexOf('Content/profanity.txt');
    if (i >= 0) useProfanityList(texts[i]);
    return game.init(names, texts);
  },
  content: () => contentForPage(),
  /** Changes whenever other content goes in effect (a world sent its own). */
  contentVersion: () => contentVersion(),
  takeMessage: () => game.takeMessage(),

  // ---- login ---------------------------------------------------------------------------------------------------
  logout: (message: string | null) => game.logout(message),
  /** Joins the world with an ID token (signed in) or as a guest (no token: the kept guest, or a new one). */
  startSpacetime: (options: SpacetimeOptions, token: string | undefined, guest: boolean) => game.startSpacetime(options, token, guest),

  // ---- joining and characters ----------------------------------------------------------------------------------
  sessionState: () => game.sessionState(),
  pump: (dt: number) => game.pump(dt),
  /** Checks a name as it's typed (format only; the server also checks banned words and uniqueness). */
  checkName: (name: string) => (name.length === 0 ? { name: null, error: null } : validateCharacterName(name)),
  createCharacter: (name: string, appearance: number) => S()?.sendCreateCharacter(name, appearance),
  selectCharacter: (name: string) => S()?.sendSelectCharacter(name),
  leaveWorld: (message: string | null, isError: boolean) => game.leaveWorld(message, isError),

  // ---- play ----------------------------------------------------------------------------------------------------
  frame: (dt: number, buttons: number, eHeld: boolean, ePressed: boolean, blocked: boolean) => game.frame(dt, buttons, eHeld, ePressed, blocked),
  takeDirtyChunks: () => game.takeDirtyChunks(),
  chunk: (cx: number, cy: number) => game.chunk(cx, cy),
  worldObjects: () => game.worldObjects(),
  loadLoginMap: (text: string) => game.loadLoginMap(text),
  backdropChunk: (cx: number, cy: number) => game.backdropChunk(cx, cy),
  attack: (worldX: number, worldY: number) => game.attack(worldX, worldY),
  releaseCorpse: () => game.releaseCorpse(),
  block: (want: boolean, worldX: number, worldY: number) => game.block(want, worldX, worldY),
  saveMapMemory: () => S()?.saveMapMemory(),
  sendChat: (text: string) => S()?.sendChat(text),
  chat: (filter: boolean) => chat(filter),
  social: () => social(),
  localMessage: (text: string) => S()?.addLocalMessage(ChatChannel.System, text),

  // ---- items ---------------------------------------------------------------------------------------------------
  moveItem: (fromC: number, from: number, toC: number, to: number) => S()?.sendInventoryMove(fromC, from, toC, to),
  quickMove: (c: number, index: number) => S()?.sendInventoryQuickMove(c, index),
  dropItem: (c: number, index: number, count: number) => S()?.sendDrop(c, index, count),
  destroyItem: (c: number, index: number, item: number) => S()?.sendDestroyItem(c, index, item),
  useItem: (c: number, index: number) => S()?.tryUseItem(c, index) ?? false,
  craft: (recipe: number, count: number, station: number) => S()?.sendCraft(recipe, count, station),
  bagTake: (bag: number, slot: number, item: number) => S()?.sendBagTake(bag, slot, item),
  bagTakeAll: (bag: number) => S()?.sendBagTakeAll(bag),
  bagDeposit: (bag: number, c: number, index: number) => S()?.sendBagDeposit(bag, c, index),
  vendorBuy: (npc: number, index: number, item: number, count: number) => S()?.sendVendorBuy(npc, index, item, count),
  vendorSell: (npc: number, c: number, index: number, item: number) => S()?.sendVendorSell(npc, c, index, item),
  vendorSellJunk: (npc: number) => S()?.sendVendorSellJunk(npc),
  /** Learns a talent, or gives one back (checked first: a refusal is a notice). */
  talentAction: (node: number, refund: boolean) => W.talentAction(node, refund),

  // ---- windows -------------------------------------------------------------------------------------------------
  inventory: () => W.inventory(),
  canPlace: (fromC: number, from: number, toC: number, to: number) => W.canPlace(fromC, from, toC, to),
  moveBinds: (fromC: number, from: number, toC: number, to: number) => W.moveBinds(fromC, from, toC, to),
  quickMoveBinds: (c: number, index: number) => W.quickMoveBinds(c, index),
  itemTip: (item: number, bound: boolean, count: number, hint: string, compare: boolean, shift: boolean) => W.itemTip(item, bound, count, hint, compare, shift),
  character: () => W.character(),
  talents: () => W.talents(),
  crafting: (station: number) => W.crafting(station),
  bag: (id: number) => W.bag(id),
  vendor: (npc: number) => W.vendor(npc),
  vendorBuyChecked: (npc: number, index: number, many: boolean) => W.vendorBuy(npc, index, many),
  vendorSellChecked: (npc: number, c: number, index: number) => W.vendorSell(npc, c, index),
  sellValue: (c: number, index: number) => W.sellValue(c, index),
  dialogue: (npc: number) => W.dialogue(npc),
  quests: () => W.quests(),
  trade: () => W.trade(),
  tradeOffer: (c: number, index: number) => W.tradeOffer(c, index),
  tradeRemove: (slot: number) => W.tradeRemove(slot),
  tradeMoney: (text: string) => W.tradeMoney(text),
  map: () => W.map(),
  mapChunk: (cx: number, cy: number) => W.mapChunk(cx, cy),
  notice(kind: string, text: string) {
    const s = S();
    s?.notices.push({ kind: kind === 'error' ? NoticeKind.Error : kind === 'success' ? NoticeKind.Success : NoticeKind.Info, text, time: s.time });
  },

  // ---- people, quests, admin -----------------------------------------------------------------------------------
  questAction: (quest: number, action: number) => S()?.sendQuestAction(quest, action),
  partyAction: (action: number, name: string) => S()?.sendPartyAction(action, name),
  ignore: (name: string, ignore: boolean) => S()?.sendIgnore(name, ignore),
  friend: (name: string, add: boolean) => S()?.sendFriend(name, add),
  tradeRequest: (name: string) => S()?.sendTradeRequest(name),
  tradeReply: (accept: boolean) => S()?.sendTradeReply(accept),
  tradeAccept: () => S()?.sendTradeAccept(),
  tradeCancel: () => S()?.sendTradeCancel(),
  admin: (action: number, arg: number, count: number, x: number, y: number, name = '') => S()?.sendAdmin(action, arg, count, x, y, name),
  /** What the admin panel lists: online players (with you first), the maps to go to, and your level. */
  adminInfo: () => {
    const s = S();
    if (!s) return { me: '', level: 1, players: [], maps: [] };
    const me = s.local.name;
    return { me, level: s.level, maps: s.adminMaps,
      players: [me, ...s.players.map(p => p.name).filter(n => n.toLowerCase() !== me.toLowerCase())].map(n => ({ name: n, zone: s.players.find(p => p.name === n)?.zoneName ?? '' })) };
  },
};
